import React from 'react';
import PropTypes from 'prop-types';
import Style from './list.module.css';

import { WeezHeader, Icon, SelectButton, Button, Card, ContentCard, SplitContainer, FilterSetController, CheckboxLabel, SelectableButton, Dropdown } from '@weezevent/nacre';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SessionsCalendar } from './components/sessionsCalendar';
import i18n from 'i18next';

import { ListPreview } from './components/listPreview';
import { DeleteAction, DeletionModal } from './../actions/deleteAction';
import { StatusAction } from './../actions/statusAction';
import TaxModel from '../../../models/taxModel';
import { DateAction } from './../actions/dateAction';
import { TaxAction } from './../actions/taxAction';
import { RatesAction } from './../actions/ratesAction';
import { QuotasAction } from './../actions/quotasAction';
import RatesTreeModel from '../../../models/ratesTreeModel';
import QuotasModel from '../../../models/quotasModel';
import SessionModel from '../../../models/sessionModel';
import LoadingCard from '../../../components/loadingCard';
import useAppContext from '../../../context/useAppContext';
import { SeeAttendeesAction } from '../../rates/components/action/seeAttendeesAction';
import { SyncButton } from '../../../components/sync-button/sync-button';

function LabelActionButton({ selected = {} }) {
    const { t } = useTranslation();

    if (!selected || Object.values(selected).length === 0) {
        return <span>{t('ticket.common.action.bulk')}</span>;
    }

    return (
        <span>
            {t('ticket.sessions.list.sessionAction', { count: Object.values(selected).length })}
            <span className={Style['label-session-action']}>{` ${t('ticket.common.action.bulk-selected')}`}</span>
        </span>
    );
}

function LabelSelectAll() {
    const { t } = useTranslation();

    return (
        <span>
            <span className={Style['label-long-all']}>{t('ticket.common.action.select-all')}</span>
            <span className={Style['label-short-all']}>{t('ticket.common.all')}</span>
        </span>
    );
}

export default function SessionsList({ organization, event }) {
    const [{ event_timezone }] = useAppContext();

    const [sessions, loading, sync] = SessionModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            query: {
                limit: 9999
            }
        },
        [organization, event]
    );

    const [taxes, ,] = TaxModel.useApiModel({});

    const [rates, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk
        },
        [organization, event]
    );

    const history = useHistory();
    const locale = i18n.language;
    const { t } = useTranslation();
    const [selected, setSelected] = React.useState({});
    const [allSelected, setAllSelected] = React.useState(false);
    const [isSelectable, setIsSelectable] = React.useState(false);
    const [anchorSelected, setAnchorSelected] = React.useState(null);
    const [datesWithSessions, setDatesWithSessions] = React.useState({});
    const [sessionToDelete, setSessionToDelete] = React.useState([]);

    const onChangeSelectable = React.useCallback(selected => {
        setAllSelected(false);
        setSelected({});
        setIsSelectable(selected);
    }, []);

    const [quotas, loadingQuotas] = QuotasModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            getForRates: true,
            cache: false,
            allow_cache: false,
            query: {
                id_billet: 0,
                id_categorie: 0
            }
        },
        [organization, event]
    );

    const onSessionSelected = React.useCallback(
        ({ uuid, checked }) => {
            setSelected(currentState => {
                const newSelected = { ...currentState };
                const isSelected = uuid in currentState;
                if (!checked && isSelected) {
                    delete newSelected[uuid];
                }

                if (checked && !isSelected) {
                    newSelected[uuid] = sessions.find(s => s.uuid === uuid)?.copy();
                }
                setAllSelected(Object.values(newSelected).length === sessions.length);

                return newSelected;
            });
        },
        [sessions]
    );

    const handleAllSelected = React.useCallback(
        checked => {
            if (!checked) {
                setAllSelected(false);
                setSelected({});
                return;
            }

            setAllSelected(true);
            setSelected(
                sessions.reduce((acc, session) => {
                    acc[session.uuid] = session.copy();
                    return acc;
                }, {})
            );
        },
        [sessions]
    );

    const onSaveAction = React.useCallback(() => {
        sync({ allow_cache: false, cache: false, query: { limit: 9999 } });
        setSelected({});
        setAllSelected(false);
    }, [sync]);

    const manualSync = React.useCallback(() => {
        sync({ allow_cache: false, cache: false, query: { sync: true, limit: 9999 } });
        setSelected({});
        setAllSelected(false);
    }, [sync]);

    const scrollToAnchor = React.useCallback(
        anchorTarget => {
            const y = (anchorTarget.getBoundingClientRect().top || 0) + window.pageYOffset - (isSelectable ? 270 : 200);
            window.scrollTo({
                top: y,
                behavior: 'smooth',
                block: 'start'
            });
        },
        [isSelectable]
    );

    // scrollToAnchor is not added to the dependencies of the useEffect
    // because the isSelectable dependency of this one could trigger
    // the useEffect when we do not want it
    React.useEffect(() => {
        if (anchorSelected) {
            let options = { year: 'numeric', month: '2-digit', day: '2-digit' };
            //locale sv-SE for return format YYYY-MM-DD
            let linkAnchor = new Intl.DateTimeFormat('sv-SE', options).format(new Date(anchorSelected));
            let anchorTarget = document.getElementById(linkAnchor);
            if (anchorTarget) {
                scrollToAnchor(anchorTarget);
            }
        }
    }, [anchorSelected]);

    return (
        <>
            <DeletionModal
                onSave={() => sync({ allow_cache: false, cache: false, query: { limit: 9999 } })}
                onClose={() => setSessionToDelete([])}
                itemsToDelete={sessionToDelete}
                totalItemsSelected={sessionToDelete.length}
            />
            <WeezHeader
                title={t('ticket.sessions.list.title')}
                rightComponent={[
                    <SyncButton key="sync-button" sync={manualSync} />,
                    <Button
                        primary
                        key="top-cta-form"
                        icon={<Icon name={'plus'} margin={false} />}
                        label={t('ticket.common.action.add')}
                        onClick={() => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/sessions/new`)}
                    />
                ]}
            >
                {t('ticket.sessions.list.subtitle')}
            </WeezHeader>
            <Card separator={false} className={`${Style['filter-container']} ${isSelectable ? Style['filter-container__with-selectable'] : ''}`}>
                <ContentCard>
                    <FilterSetController
                        total={loading ? ` – ` : sessions.length}
                        labelTotal={t('ticket.common.sessions', { count: loading ? '-' : sessions.length })}
                        labelReset={t('ticket.common.action.reset')}
                        labelFilters={t('ticket.common.cta.filters')}
                        search={{
                            onChange: (synth, value) => {},
                            handleReset: (synth, value) => {},
                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: ''
                        }}
                        onChange={values => {}}
                        initialValues={() => {}}
                        defaultValues={null}
                        onReset={() => {}}
                        leftComponent={<SelectableButton onClick={selected => onChangeSelectable(selected)} label={t('ticket.common.action.select')} isSelected={isSelectable} />}
                    />
                </ContentCard>
            </Card>
            <LoadingCard className={Style['loading']} loading={loading || loadingQuotas}>
                {Boolean(sessions && sessions.length) && isSelectable && (
                    <div className={`${Style['global-action__container']} ${Object.keys(selected).length ? Style['with-selected'] : ''}`}>
                        <SelectButton
                            rates={rates}
                            tooltipTitle={t('ticket.sessions.list.noActionTooltip')}
                            title={<LabelActionButton selected={selected} />}
                            objects={Object.values(selected)}
                        >
                            <SeeAttendeesAction model={SessionModel} filterKey={'booking__inventory__in'} />
                            <RatesAction rates={rates} loadingRates={loadingRates} onSave={onSaveAction} />
                            <QuotasAction rates={rates} loadingRates={loadingRates} onSave={onSaveAction} />
                            <Dropdown.Line />
                            <DateAction timezone={event.timezone} onSave={onSaveAction} />
                            <TaxAction taxes={taxes} onSave={onSaveAction} />
                            <StatusAction onSave={onSaveAction} />
                            <StatusAction action={'disable'} onSave={onSaveAction} />
                            <Dropdown.Line />
                            <DeleteAction onSave={onSaveAction} />
                        </SelectButton>
                        <CheckboxLabel
                            className={Style['select-all']}
                            onChange={(event, { checked }) => handleAllSelected(checked)}
                            key={'select-all'}
                            label={<LabelSelectAll />}
                            name={'select-all'}
                            checked={allSelected}
                        />
                    </div>
                )}
                <div style={{ color: 'gray', height: '20px', zIndex: '2' }}></div>
                <SplitContainer
                    className={`${Style['split-container__list']} ${isSelectable ? Style['split-container__list-with-selectable'] : ''}`}
                    displayAdditional
                    onlyDesktop
                    additional={
                        !loading && (
                            <ListPreview
                                anchorSelected={anchorSelected}
                                onChangeAnchorSelected={setAnchorSelected}
                                datesWithSessions={datesWithSessions}
                                selectable={isSelectable}
                            />
                        )
                    }
                >
                    <SessionsCalendar
                        sessions={sessions}
                        quotas={quotas}
                        locale={locale}
                        timezone={event_timezone}
                        selected={Object.keys(selected)}
                        onSessionSelected={onSessionSelected}
                        selectable={isSelectable}
                        onChangeDatesWithSessions={setDatesWithSessions}
                        onChangeAnchorSelected={setAnchorSelected}
                        setSessionToDelete={setSessionToDelete}
                    />
                </SplitContainer>
            </LoadingCard>
        </>
    );
}

SessionsList.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
